import { useCallback, useMemo, useState } from 'react';

import { Button } from '@mui/material';

import { Avatar } from '@karehero/llama/components/atoms/Avatar/Avatar';
import { Icon } from '@karehero/llama/components/atoms/Icon/Icon';
import {
  type GridColDef,
  type GridRenderCellParams,
} from '@karehero/llama/pwa/organisms/DataTable/DataTable';
import AdminAccountsPage, {
  type ResolvedCareCircleMember,
} from '@karehero/llama/pwa/pages/AdminAccountsPage/AdminAccountsPage';

import {
  useDeleteAccountMutation,
  useGetAllAccountQuery,
} from 'store/api/account';
import { useGetCareCircleQuery } from 'store/api/careCircle';
import { Account, CareCircleMember, PaginationParams } from '@karehero/models';
import { newToast } from 'store/slices/uiSlice';
import { useDispatch } from 'react-redux';
import { prettyKindeIdentityType } from '@karehero/models';
import { getProfilePictureUrl } from '@karehero/llama/pwa/utils/getProfilePictureUrl';
import { formatRoleName } from '@karehero/llama/utils/format';
import { useNavigate } from 'react-router-dom';
import { ROUTES, DYNAMIC_ROUTES } from 'router';

const columns: GridColDef[] = [
  {
    field: 'imgSrc',
    headerName: '',
    width: 72,
    sortable: false,
    filterable: false,
    disableColumnMenu: true,
    renderCell: (params: GridRenderCellParams) => {
      const row = params.row;
      const fallback =
        `${row.firstName?.[0] || ''}${row.lastName?.[0] || ''}`.toUpperCase();
      return (
        <Avatar size='xs' src={getProfilePictureUrl(row)} fallback={fallback} />
      );
    },
  },
  { field: 'fullName', headerName: 'Name', flex: 1, minWidth: 150 },
  { field: 'email', headerName: 'Email', flex: 1, minWidth: 200 },
  {
    field: 'kindeEmail',
    headerName: 'Kinde Email',
    flex: 1,
    minWidth: 180,
    valueGetter: (_value: any, row: any) => row.kindeEmail || 'Not migrated',
  },
  {
    field: 'kindeAuthType',
    headerName: 'Auth',
    flex: 0.5,
    minWidth: 100,
    valueGetter: (_value: any, row: any) =>
      prettyKindeIdentityType(row.kindeAuthType),
  },
  {
    field: 'careCircleMembers',
    headerName: 'Member Roles',
    flex: 1,
    minWidth: 150,
    valueGetter: (_value: any, row: any) => {
      const rolesM: { [k: string]: boolean } = {};
      row.careCircleMembers?.forEach((member: any) => {
        member.roles?.forEach((role: any) => {
          rolesM[role.name] = true;
        });
      });
      const roles = Object.keys(rolesM);
      roles.sort();
      return roles.join('; ');
    },
  },
  {
    field: 'accountRole',
    headerName: 'Account Role',
    flex: 0.7,
    minWidth: 120,
    valueGetter: (_value: any, row: any) =>
      formatRoleName(row.accountRole?.name),
  },
  {
    field: 'organization',
    headerName: 'Organisation',
    flex: 0.7,
    minWidth: 130,
    valueGetter: (_value: any, row: any) => row.organization?.name,
  },
  {
    field: 'isOnboarded',
    headerName: 'Onboarded',
    flex: 0.5,
    minWidth: 140,
    valueGetter: (_value: any, row: any) => (row.isOnboarded ? 'Yes' : 'No'),
  },
  {
    field: 'kindeCreatedOn',
    headerName: 'Kinde Created',
    flex: 0.7,
    minWidth: 180,
    valueGetter: (_value: any, row: any) => {
      const createdOn = row.kindeUser?.createdOn;
      if (!createdOn) return '';
      return new Date(createdOn).toLocaleDateString();
    },
  },
];

const useResolvedMembers = (
  members: CareCircleMember[],
): ResolvedCareCircleMember[] => {
  const queries = members.map((m) => useGetCareCircleQuery(m.careCircleID));
  return members.map((m, i) => ({
    ...m,
    careCircle: queries[i].data,
    isLoading: queries[i].isLoading,
  }));
};

const AccountDrawerConnector = ({
  account,
  children,
}: {
  account: Account;
  children: (members: ResolvedCareCircleMember[]) => React.ReactNode;
}) => {
  const resolved = useResolvedMembers(account.careCircleMembers ?? []);
  return <>{children(resolved)}</>;
};

export const ManageAccounts = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [deleteTargetID, setDeleteTargetID] = useState<string | null>(null);
  const [drawerAccountID, setDrawerAccountID] = useState<string | null>(null);
  const [paginationParams, setPaginationParams] = useState<PaginationParams>({
    page: 1,
    perPage: 10,
  });

  const {
    data: accountsData,
    isFetching,
    isError,
  } = useGetAllAccountQuery(paginationParams);
  const accounts = accountsData?.data;
  const [deleteAccount] = useDeleteAccountMutation();

  const deleteTarget = useMemo(
    () => accounts?.find((a) => a.id === deleteTargetID) ?? null,
    [deleteTargetID, accounts],
  );

  const drawerAccount = useMemo(
    () => accounts?.find((a) => a.id === drawerAccountID) ?? null,
    [drawerAccountID, accounts],
  );

  const rows = useMemo(() => {
    if (!accounts) return [];
    return accounts.map((account) => ({ ...account }));
  }, [accounts]);

  const handleDelete = useCallback(async () => {
    if (!deleteTargetID) return;
    const res = await deleteAccount({ id: deleteTargetID });
    if ('error' in res) {
      console.error(res.error);
      setDeleteTargetID(null);
      return;
    }
    dispatch(
      newToast({
        title: 'Account Deleted',
        description: 'Account successfully deleted',
        variant: 'success',
      }),
    );
    setDeleteTargetID(null);
  }, [deleteTargetID, deleteAccount, dispatch]);

  const handlePaginationChange = useCallback(
    (page: number, pageSize: number) => {
      setPaginationParams((prev) => ({
        ...prev,
        page: page + 1,
        perPage: pageSize,
      }));
    },
    [],
  );

  const handleSearchChange = useCallback((search: string) => {
    setPaginationParams((prev) => ({ ...prev, search, page: 1 }));
  }, []);

  const handleDrawerClose = useCallback(() => {
    setDrawerAccountID(null);
  }, []);

  const renderPage = (drawerCareCircles: ResolvedCareCircleMember[]) => (
    <AdminAccountsPage
      columns={columns}
      rows={rows}
      actions={[
        {
          label: 'View',
          icon: 'eye',
          onPress: (account: Account) => setDrawerAccountID(account.id),
        },
        {
          label: 'Delete',
          icon: 'trash',
          onPress: (account: Account) => setDeleteTargetID(account.id),
        },
        {
          label: 'Verify',
          icon: 'badge-check',
          onPress: (account: Account) =>
            navigate(DYNAMIC_ROUTES.ADMIN_ACCOUNT_VERIFY(account.id)),
        },
        {
          label: 'New Care Circle',
          icon: 'circle-plus',
          onPress: (account: Account) =>
            navigate(DYNAMIC_ROUTES.ADMIN_CARE_CIRCLE_NEW(account.id)),
        },
      ]}
      toolbarActions={
        <Button
          variant='contained'
          size='small'
          onClick={() => navigate(ROUTES.ADMIN_ACCOUNT_NEW)}
          startIcon={<Icon icon='plus' size='sm' />}
        >
          New Account
        </Button>
      }
      drawerAccount={drawerAccount}
      drawerCareCircles={drawerCareCircles}
      onDrawerClose={handleDrawerClose}
      onCareCircleClick={(id) =>
        navigate(DYNAMIC_ROUTES.ADMIN_CARE_CIRCLE_VIEW(id))
      }
      deleteEmail={deleteTarget?.email}
      isDeleteOpen={Boolean(deleteTarget)}
      onDeleteClose={() => setDeleteTargetID(null)}
      onDeleteConfirm={handleDelete}
      paginationMode='server'
      rowCount={accountsData?.pagination?.totalItems}
      page={(paginationParams.page ?? 1) - 1}
      onPaginationChange={handlePaginationChange}
      rowsPerPage={paginationParams.perPage ?? 10}
      isLoading={isFetching}
      isError={isError}
      onSearchChange={handleSearchChange}
      searchValue={paginationParams.search ?? ''}
    />
  );

  if (drawerAccount) {
    return (
      <AccountDrawerConnector key={drawerAccount.id} account={drawerAccount}>
        {renderPage}
      </AccountDrawerConnector>
    );
  }

  return renderPage([]);
};

export default ManageAccounts;
